import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';
import {
	defaultMST2020_R01Dto,
	MST2020_R01Dto,
} from './Dto_MST2020_R01';

/** 검색 조건 */
export interface MST2020_R01Filter {
	areaCd: string;
	customCd: string;
	customNm: string;
}

export interface Store_MST2020_R01 {
	MST2020_R01Filter: MST2020_R01Filter;
	MST2020_R01PastFilter: MST2020_R01Filter;
	setMST2020_R01Filter: (
		filter: Partial<MST2020_R01Filter>,
	) => void;
	setMST2020_R01PastFilter: (
		filter: Partial<MST2020_R01Filter>,
	) => void;

	MST2020_R01List: MST2020_R01Dto[];
	setMST2020_R01List: (
		list: MST2020_R01Dto[],
	) => void;
	insertMST2020_R01List: (
		data: MST2020_R01Dto[],
		index?: number,
	) => void;
	removeMST2020_R01List: (index: number) => void;
	updateMST2020_R01List: (
		index: number,
		data: MST2020_R01Dto,
	) => void;

	deletedMST2020_R01List: string[];
	addDeletedMST2020_R01List: (
		idList: string[],
	) => void;
	removeDeletedMST2020_R01List: (
		idList?: string[],
	) => void;

	selectedMST2020_R01List: string[];
	setSelectedMST2020_R01List: (
		idList: string[],
	) => void;
	addSelectedMST2020_R01List: (
		idList: string[],
	) => void;
	removeSelectedMST2020_R01List: (
		idList?: string[],
	) => void;
	initSelectedMST2020_R01List: () => void;
	allSelectedMST2020_R01List: () => void;

	originalMST2020_R01List: MST2020_R01Dto[];
	setOriginalMST2020_R01List: (
		list: MST2020_R01Dto[],
	) => void;

	columnState: ColumnState[] | null;
	setColumnState: (
		state: ColumnState[] | null,
	) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;

	allCount: number;
	setAllCount: (allCount: number) => void;

	initData: () => void;
}

export const defaultMST2020_R01Filter: MST2020_R01Filter = {
	areaCd: '',
	customCd: '',
	customNm: '',
};

export const useMST2020_R01Store =
	create<Store_MST2020_R01>((set) => ({
		MST2020_R01Filter: defaultMST2020_R01Filter,
		MST2020_R01PastFilter: defaultMST2020_R01Filter,

		setMST2020_R01Filter: (update) =>
			set((state) => ({
				MST2020_R01Filter: {
					...state.MST2020_R01Filter,
					...update,
				},
			})),

		setMST2020_R01PastFilter: (update) =>
			set((state) => ({
				MST2020_R01PastFilter: {
					...state.MST2020_R01PastFilter,
					...update,
				},
			})),

		MST2020_R01List: [],

		setMST2020_R01List: (MST2020_R01List) =>
			set((state) => {
				const newList: MST2020_R01Dto[] = [];
				let valueAndOrderIndex = 1;

				MST2020_R01List.forEach((item) => {
					newList.push({
						...defaultMST2020_R01Dto,
						...item,
						valueIndex:
							item.valueIndex > 0
								? item.valueIndex
								: valueAndOrderIndex,
						orderIndex:
							item.orderIndex > -1
								? item.orderIndex
								: valueAndOrderIndex,
						orderIndexSeq:
							item.orderIndexSeq > 0
								? item.orderIndexSeq
								: 0,
					});

					valueAndOrderIndex++;
				});

				return {
					...state,
					MST2020_R01List: newList,
				};
			}),

		insertMST2020_R01List: (data, index) =>
			set((state) => {
				let newList = [
					...state.MST2020_R01List,
				];

				const targetRowData = newList.find(
					(item) => item.valueIndex === index,
				);

				const targetOrderIndex =
					targetRowData?.orderIndex || 0;

				const newOrderIndexSeq =
					(targetRowData?.orderIndexSeq || 0) + 1;

				let maxValueIndex =
					Math.max(
						0,
						...newList.map(
							(item) => item.valueIndex || 0,
						),
					) + 1;

				const targetRows = newList.filter(
					(item) =>
						item.orderIndex
						=== targetOrderIndex,
				);

				targetRows.forEach((item) => {
					if (
						item.orderIndexSeq
						>= newOrderIndexSeq
					) {
						item.orderIndexSeq =
							item.orderIndexSeq + 1;
					}
				});

				newList = newList.filter(
					(item) =>
						item.orderIndex
						!== targetOrderIndex,
				);

				newList.push(...targetRows);

				const newData = data.map((item) => ({
					...defaultMST2020_R01Dto,
					...item,
					valueIndex: maxValueIndex++,
					orderIndex: targetOrderIndex,
					orderIndexSeq: newOrderIndexSeq,
				}));

				if (index !== undefined) {
					newList.splice(index, 0, ...newData);
				} else {
					newList.push(...newData);
				}

				return {
					...state,
					MST2020_R01List: newList,
				};
			}),

		removeMST2020_R01List: (index) =>
			set((state) => ({
				...state,
				MST2020_R01List:
					state.MST2020_R01List.filter(
						(item) =>
							item.valueIndex !== index,
					),
			})),

		updateMST2020_R01List: (valueIndex, data) =>
			set((state) => ({
				...state,
				MST2020_R01List:
					state.MST2020_R01List.map(
						(item) =>
							item.valueIndex === valueIndex
								? data
								: item,
					),
			})),

		deletedMST2020_R01List: [],

		addDeletedMST2020_R01List: (idList) =>
			set((state) => {
				const deletedList = [
					...state.deletedMST2020_R01List,
				];

				for (const valueIndex of idList) {
					const isInserted =
						state.MST2020_R01List.find(
							(item) =>
								item.valueIndex
									=== Number(valueIndex)
								&& item.inserted,
						);

					const isModified =
						state.MST2020_R01List.find(
							(item) =>
								item.valueIndex
									=== Number(valueIndex)
								&& item.isModified,
						);

					if (!isInserted && !isModified) {
						deletedList.push(valueIndex);
					}
				}

				return {
					...state,
					deletedMST2020_R01List:
						deletedList,
				};
			}),

		removeDeletedMST2020_R01List: (idList) =>
			set((state) => ({
				...state,
				deletedMST2020_R01List:
					state.deletedMST2020_R01List.filter(
						(value) =>
							idList
								? !idList.includes(value)
								: false,
					),
			})),

		selectedMST2020_R01List: [],

		setSelectedMST2020_R01List: (idList) =>
			set({
				selectedMST2020_R01List: idList,
			}),

		addSelectedMST2020_R01List: (idList) =>
			set((state) => ({
				...state,
				selectedMST2020_R01List: [
					...state.selectedMST2020_R01List,
					...idList,
				],
			})),

		removeSelectedMST2020_R01List: (idList) =>
			set((state) => ({
				...state,
				selectedMST2020_R01List:
					state.selectedMST2020_R01List.filter(
						(value) =>
							idList
								? !idList.includes(value)
								: false,
					),
			})),

		initSelectedMST2020_R01List: () =>
			set({
				selectedMST2020_R01List: [],
			}),

		allSelectedMST2020_R01List: () =>
			set((state) => ({
				...state,
				selectedMST2020_R01List:
					state.MST2020_R01List.map(
						(item) =>
							item.valueIndex.toString(),
					),
			})),

		originalMST2020_R01List: [],

		setOriginalMST2020_R01List:
			(MST2020_R01List) =>
				set((state) => {
					const newList: MST2020_R01Dto[] = [];
					let valueAndOrderIndex = 1;

					MST2020_R01List.forEach((item) => {
						newList.push({
							...defaultMST2020_R01Dto,
							...item,
							valueIndex:
								valueAndOrderIndex,
							orderIndex:
								valueAndOrderIndex,
							orderIndexSeq:
								valueAndOrderIndex,
						});

						valueAndOrderIndex++;
					});

					return {
						...state,
						originalMST2020_R01List:
							newList,
					};
				}),

		columnState: null,

		setColumnState: (columnState) =>
			set({ columnState }),

		scrollTop: null,

		setScrollTop: (scrollTop) =>
			set({ scrollTop }),

		allCount: 0,

		setAllCount: (allCount) =>
			set({ allCount }),

		initData: () =>
			set((state) => ({
				...state,
				MST2020_R01Filter:
					defaultMST2020_R01Filter,
				MST2020_R01PastFilter:
					defaultMST2020_R01Filter,
				MST2020_R01List: [],
				selectedMST2020_R01List: [],
				deletedMST2020_R01List: [],
				allCount: 0,
				scrollTop: null,
			})),
	}));
